
import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { collection, addDoc, serverTimestamp, query, where, onSnapshot, orderBy, updateDoc, deleteDoc, doc, getDoc, increment, getDocs } from 'firebase/firestore';
import { useTransactionManager } from './useTransactionManager';

export interface P2PListing {
    id: string;
    type: 'BUY' | 'SELL';
    advertiserName: string;
    price: number;
    available: number;
    limitMin: number;
    limitMax: number;
    rating: number;
    trades: number;
    asset: string;
    status: 'Active' | 'Inactive';
}

export const useP2P = (userId?: string) => {
    const [listings, setListings] = useState<P2PListing[]>([]);
    const [allListings, setAllListings] = useState<P2PListing[]>([]); // 어드민용 전체 광고
    const [myTrades, setMyTrades] = useState<any[]>([]);
    const [allTrades, setAllTrades] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const { addTransaction } = useTransactionManager(userId);

    // 유저용 활성 광고 조회 (인덱스 에러 방지를 위해 orderBy 제거 고려)
    useEffect(() => {
        const q = query(
            collection(db, 'p2p_ads'),
            where('status', '==', 'Active')
        );

        const unsubscribe = onSnapshot(q,
            (snapshot) => {
                const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as P2PListing[];
                setListings(items);
                setLoading(false);
            },
            (error) => {
                console.error("P2P Listings error:", error);
                setLoading(false); // 에러 발생 시에도 스피너 제거
            }
        );

        return () => unsubscribe();
    }, []);

    // 어드민용 전체 광고 조회
    useEffect(() => {
        const q = query(collection(db, 'p2p_ads'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q,
            (snapshot) => {
                const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as P2PListing[];
                setAllListings(items);
            },
            (error) => {
                console.error("All Listings error:", error);
            }
        );
        return () => unsubscribe();
    }, []);

    // 유저 자신의 거래 내역 조회
    useEffect(() => {
        if (!userId) return;
        const q = query(
            collection(db, 'p2p_trades'),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMyTrades(items);
        }, (error) => console.error("My Trades error:", error));

        return () => unsubscribe();
    }, [userId]);

    // 모든 거래 내역 조회 (어드민용)
    useEffect(() => {
        const q = query(collection(db, 'p2p_trades'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllTrades(items);
        }, (error) => console.error("All Trades error:", error));
        return () => unsubscribe();
    }, []);

    // 어드민 전용 광고 관리 함수
    const addAd = async (data: Partial<P2PListing>) => {
        await addDoc(collection(db, 'p2p_ads'), {
            ...data,
            status: 'Active',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
    };

    const updateAd = async (id: string, data: Partial<P2PListing>) => {
        await updateDoc(doc(db, 'p2p_ads', id), {
            ...data,
            updatedAt: serverTimestamp()
        });
    };

    const deleteAd = async (id: string) => {
        // 실제 삭제 대신 상태 변경 추천하지만 요청에 따라 문서 삭제 진행
        await deleteDoc(doc(db, 'p2p_ads', id));
    };

    // 거래 관련 함수
    const updateTradeStatus = async (tradeId: string, status: string) => {
        const tradeRef = doc(db, 'p2p_trades', tradeId);

        if (status === 'COMPLETED') {
            const tradeSnap = await getDoc(tradeRef);
            if (tradeSnap.exists()) {
                const tradeData = tradeSnap.data();
                const { userId, type, asset, totalKrw, totalCoin, transactionId } = tradeData;
                const userRef = doc(db, 'users', userId);
                const assetKey = asset.toLowerCase();

                // 밸런스 업데이트 (매수/매도 분기)
                if (type === 'BUY') {
                    await updateDoc(userRef, {
                        krw: increment(-totalKrw),
                        [assetKey]: increment(totalCoin)
                    });
                } else if (type === 'SELL') {
                    await updateDoc(userRef, {
                        krw: increment(totalKrw),
                        [assetKey]: increment(-totalCoin)
                    });
                }

                // 관련 트랜잭션 기록 업데이트
                if (transactionId) {
                    await updateDoc(doc(db, 'transactions', transactionId), {
                        status: 'APPROVED', // COMPLETED 대신 APPROVED 사용 (기존 컨벤션 확인)
                        updatedAt: serverTimestamp()
                    });
                } else {
                    // [복구 로직] transactionId가 없는 예전 데이터의 경우 검색하여 업데이트
                    try {
                        const txQuery = query(
                            collection(db, 'transactions'),
                            where('userId', '==', userId),
                            where('status', '==', 'PENDING'),
                            where('type', '==', 'SWAP')
                        );
                        const txSnap = await getDocs(txQuery);
                        const targetTx = txSnap.docs.find(d => {
                            const data = d.data();
                            return data.asset.includes(asset) && (data.amount.toString().includes(totalCoin.toString()) || data.amount.toString().includes(totalKrw.toString()));
                        });

                        if (targetTx) {
                            await updateDoc(doc(db, 'transactions', targetTx.id), {
                                status: 'APPROVED',
                                updatedAt: serverTimestamp()
                            });
                            // 추후 중복 추적 방지를 위해 trade 문서에도 txId 기록
                            await updateDoc(tradeRef, { transactionId: targetTx.id });
                        }
                    } catch (e) {
                        console.error("Migration/Sync error:", e);
                    }
                }
            }
        } else if (status === 'CANCELLED') {
            const tradeSnap = await getDoc(tradeRef);
            if (tradeSnap.exists()) {
                const { transactionId } = tradeSnap.data();
                if (transactionId) {
                    await updateDoc(doc(db, 'transactions', transactionId), {
                        status: 'REJECTED',
                        updatedAt: serverTimestamp()
                    });
                }
            }
        }

        await updateDoc(tradeRef, {
            status,
            updatedAt: serverTimestamp()
        });
    };

    const executeTrade = async (listing: P2PListing, amountKrw: number, amountCoin: number) => {
        if (!userId) throw new Error("User not authenticated");
        try {
            // 1. 트랜잭션 기록 생성 (먼저 생성하여 ID 확보)
            const txId = await addTransaction({
                userId,
                type: 'SWAP',
                asset: `P2P ${listing.type === 'SELL' ? '매수' : '매도'}: ${listing.asset}`,
                amount: listing.type === 'SELL' ? `+${amountCoin}` : `-${amountCoin}`,
                status: 'PENDING'
            });

            // 2. P2P 거래 문서 생성 (txId 포함)
            const tradeRef = await addDoc(collection(db, 'p2p_trades'), {
                userId,
                listingId: listing.id,
                advertiserName: listing.advertiserName,
                type: listing.type === 'SELL' ? 'BUY' : 'SELL',
                totalKrw: amountKrw,
                totalCoin: amountCoin,
                asset: listing.asset,
                status: 'PROCESSING',
                transactionId: txId || null,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            return { success: true, id: tradeRef.id };
        } catch (error) {
            console.error("P2P Trade error:", error);
            return { success: false, error };
        }
    };

    return {
        listings,
        allListings,
        myTrades,
        allTrades,
        loading,
        addAd,
        updateAd,
        deleteAd,
        updateTradeStatus,
        executeTrade
    };
};
